import React from 'react'
import { PageProps, graphql } from 'gatsby'
import { Trans, useTranslation } from 'react-i18next'
import { MapOutcomesQuery } from '@graphql-types'
import { css, useTheme } from '@emotion/react'
import MAPLayout from './_layout'
import { GatsbyImage } from 'gatsby-plugin-image'
import { maxWidth, responsiveColumn } from 'src/themes'
import { ScrollAnimation } from '@components/ScrollAnimation'
import { H2Highlight } from '@components/H2Highlight'
import { FaLinkedin } from 'react-icons/fa'
import { useCountry } from './_locator'

const MAPOutcomesPage = (props: PageProps<MapOutcomesQuery>) => {
	const [currentCountry] = useCountry()
	const { t } = useTranslation()
	const theme = useTheme()

	const course = props.data.course!
	return (
		<MAPLayout course={course}>
			<h2>{t('courses:common.outcomes')}</h2>

			<h3
				css={css`
					text-align: center;
					color: ${theme.highlightColor};
				`}>
				<Trans i18nKey='courses:map.outcomes.graduate_performance.title'></Trans>
			</h3>

			<p>
				<Trans i18nKey='courses:map.outcomes.graduate_performance.description'></Trans>
			</p>

			<div
				css={css`
					display: flex;
					width: 100%;
					gap: 1rem;

					${maxWidth[2]} {
						display: block;
					}

					h3 {
						text-align: center;
						color: ${theme.highlightColor};
					}

					> div {
						flex: 1;
					}
				`}>
				<div>
					<h3>{t('courses:map.outcomes.showcase.title')}</h3>
					<div
						css={css`
							position: relative;
							width: 100%;
							height: 0;
							padding-bottom: 56.25%;
							margin-bottom: 1.5rem;

							iframe {
								position: absolute;
								top: 0;
								left: 0;
								width: 100%;
								height: 100%;
							}
						`}>
						<iframe
							width='100%'
							height='315'
							src='https://www.youtube.com/embed/videoseries?list=PLjB7odcPYBI3uNphMDXjup1sIqkJucsck'
							title={t('courses:map.outcomes.showcase.title')}
							frameBorder='0'
							allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
							allowFullScreen></iframe>
					</div>
				</div>

				<div>
					<h3>{t('courses:map.outcomes.openday.title')}</h3>
					<div
						css={css`
							position: relative;
							width: 100%;
							height: 0;
							padding-bottom: 56.25%;
							margin-bottom: 1.5rem;

							iframe {
								position: absolute;
								top: 0;
								left: 0;
								width: 100%;
								height: 100%;
							}
						`}>
						<iframe
							width='100%'
							height='315'
							src='https://www.youtube.com/embed/videoseries?list=PLjB7odcPYBI0O0A_YJquj5G-_PIUop9oT'
							title={t('courses:map.outcomes.openday.title')}
							frameBorder='0'
							allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
							allowFullScreen></iframe>
					</div>
				</div>
			</div>

			<h3>{t('courses:map.outcomes.hiring.title')}</h3>
			<div
				css={css`
					display: flex;
					flex-wrap: wrap;
					justify-content: center;
				`}>
				{props.data.hiringPartners?.logosSharp?.map((logo, i) => (
					<div
						key={logo.childImageSharp?.id}
						css={css`
							margin: 0.5rem;
							padding: 0.5rem;
							background-color: white;
							border-radius: 1rem;
						`}>
						<ScrollAnimation animateIn='flip'>
							<GatsbyImage
								image={logo.childImageSharp?.gatsbyImageData}
								alt={
									props.data.hiringPartners?.logos?.[i]?.meta
										.title
								}
							/>
						</ScrollAnimation>
					</div>
				))}
			</div>

			{currentCountry !== 'uk' && (
				<div
					css={css`
						display: flex;
						width: 100%;
						${responsiveColumn};

						> div {
							flex: 1;
							display: flex;
							flex-direction: column;
							align-items: center;

							> h3 {
								color: ${theme.highlightColor};
							}

							p {
								margin: 1.5rem 0;
								font-size: 4rem;
								text-align: center;

								${maxWidth[2]} {
									font-size: 3rem;
								}
							}
						}
					`}>
					<div>
						<h3
							css={css`
								color: ${theme.highlightColor};
							`}>
							{t(
								'courses:map.outcomes.average_time_get_hired.title'
							)}
						</h3>
						<ScrollAnimation animateIn='fadeInDown'>
							<p>
								{t(
									'courses:map.outcomes.average_time_get_hired.description'
								)}
							</p>
						</ScrollAnimation>
					</div>
					<div>
						<h3
							css={css`
								color: ${theme.highlightColor};
							`}>
							{t('courses:map.outcomes.average_salary.title')}
						</h3>
						<ScrollAnimation animateIn='fadeInDown'>
							<p>
								{currentCountry === 'hk' &&
									t(
										'courses:map.outcomes.average_salary.description'
									)}
							</p>
						</ScrollAnimation>
					</div>
				</div>
			)}

			<H2Highlight>
				{t('courses:map.outcomes.testimonials.title')}
			</H2Highlight>

			<div
				css={css`
					h3 {
						display: flex;
						align-items: center;
						> small {
							display: inline-block;
							margin: 0 1rem;
						}
						> a {
							display: inline-block;
							vertical-align: middle;
						}
					}
				`}>
				<div>
					<h3>
						Hayton Hung <small>(Cohort 16)</small>{' '}
						<a
							href='https://www.linkedin.com/in/hayton-hung-11b19a9a/'
							target='_blank'
							title="Hayton Hung's Linkedin">
							<FaLinkedin />
						</a>
					</h3>
					<p>
						"Most appealing aspect of Tecky Academy curriculum is
						the project-based learning and career-driven nature. We
						get the opportunity to learn concepts, become aware of
						relevant tools and apply all we learn in the form of
						three group projects. Instructors are of high caliber,
						took complex topics and presented them in a very easily
						understandable manner. Another centerpiece of Tecky
						Academy programme is the one-on-one career support.
						Thanks to the full range of advice and support from
						professional career coaches, I received a job offer as
						an Analyst Programmer before graduation. "
					</p>
				</div>
				<div>
					<h3>
						Bob Chan <small>(Cohort 15)</small>{' '}
						<a
							href='https://www.linkedin.com/in/siu-hei-bob-chan-694514126/'
							target='_blank'
							title="Bob Chan's Linkedin">
							<FaLinkedin />
						</a>
					</h3>
					<p>
						"I had had my doubt at first, as any normal person would
						doubt that 4 months of training can equip people with
						skills and tech that are way beyond beginners. And after
						4months, I can comfortably say the course is a bargain
						for any programmer wanna-be. Don't get me wrong: the
						course is tough, but the lectures, group projects and ,
						most important of all, the career assistance provided
						throughout the course are extremely helpful to any
						beginner/intermediate developers."
					</p>
				</div>

				<div>
					<h3>
						Roy Tang <small>(Cohort 13)</small>{' '}
						<a
							href='https://www.linkedin.com/in/roy-tang-075607101/'
							target='_blank'
							title="Roy Tang's Linkedin">
							<FaLinkedin />
						</a>
					</h3>
					<p>
						"Tecky is a place that can enhance your effort to learn
						programming as a beginner. Well structured curriculum
						&amp; instructor support saves your time to acquire the
						skills at full speed. At the end of the course, students
						will have 3 projects in their portfolios &amp; career
						support is provided to help build resumes and prepare
						for interviews. I got a developer jobs in a few days
						after the course ended."
					</p>
				</div>

				<div>
					<h3>
						Ray Tsoi <small>(Cohort 10)</small>{' '}
						<a
							href='https://www.linkedin.com/in/yukchit/'
							target='_blank'
							title="Ray Tsoi's Linkedin">
							<FaLinkedin />
						</a>
					</h3>
					<p>
						"Very intensive &amp; useful course. Great supporting
						and teaching from experienced instructors & friendly
						tutors. Instructors would give code reviews for all the
						finished projects. Tecky Academy is not just a bootcamp,
						it is more like a community. Alumni are willing to
						support & share their own experiences as well. 10/10.."
					</p>
				</div>

				<div>
					<h3>
						Eric Chow <small>(Cohort 8)</small>{' '}
						<a
							href='https://www.linkedin.com/in/chow-kaho-89550454/'
							target='_blank'
							title="Eric Chow's Linkedin">
							<FaLinkedin />
						</a>
					</h3>
					<p>
						"I worked for customer service field almost 8 years when
						I decided to learn a new skill and enrolled to Tecky
						Academy. The bootcamps require you to give 200% of your
						life during those 4 months, but it's definitely worth
						it. They push you to the limit, all the while
						maintaining an amazing informal and casual relationship
						with the class. They give you the tools to succeed, but
						the work needs to come from you. This is much more than
						just a regular Bootcamp, it makes you a better person
						overall and gives you the tools to thrive in software
						development, you just need to have the guts and the will
						to take it and make your own path. I was afraid that my
						34 years old was somehow an obstacle to hiring, but the
						truth is that after a month of the finished course I had
						the best offer I had seen so far."
					</p>
				</div>

				<div>
					<h3>
						Harry Chow <small>(Cohort 6)</small>{' '}
						<a
							href='https://www.linkedin.com/in/harry-chow/'
							target='_blank'
							title="Harry Chow's Linkedin">
							<FaLinkedin />
						</a>
					</h3>
					<p>
						"In terms of teaching quality, tutors in Tecky are
						passionate and professional, what they care is the
						learning curve of their students, what they don't care
						is about overtime work. They usually stay until
						mid-night to help students out, no matter it is about
						project ideas, CS concept, coding styles, etc. On thing
						to highlight is that, the tutors (like Gordon, Alex) are
						really good at explaining concept, just like
						story-telling. It is very important to those who want to
						learn coding from zero. For the part of career support,
						I kept going to Tecky even though the course is
						finished. I kept asking for help in terms of CV,
						expected salary, interviews, etc, and they always
						welcome these kinds of conversation."
					</p>
				</div>

				<div>
					<h3>
						John Chan <small>(Cohort 2)</small>{' '}
						<a
							href='https://www.linkedin.com/in/johncyh/'
							target='_blank'
							title="John Chan's Linkedin">
							<FaLinkedin />
						</a>
					</h3>
					<p>
						"Both the passionate instructors and batch mates keep me
						up in the long long learning journey on the way to be a
						sophisticated software engineer. What I got is not just
						the programming knowledge but also the art of writing
						articulate codes and science of computer."
					</p>
				</div>
			</div>
		</MAPLayout>
	)
}

export default MAPOutcomesPage

export const query = graphql`
	query MAPOutcomes($language: String!) {
		...TranslationFragment
		...MAPCoursePageFragment
		hiringPartners: cockpitHiringPartnersCollection(
			_lang: { eq: $language }
		) {
			logos {
				path
				meta
			}
			logosSharp {
				childImageSharp {
					id
					gatsbyImageData(
						width: 80
						height: 80
						placeholder: BLURRED
						backgroundColor: "transparent"
						transformOptions: { fit: CONTAIN }
					)
				}
			}
		}
	}
`
